import { isNil, get } from 'lodash';

/**
 * Gets the id of the annotation.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {string} The id of the annotation or an empty string if id is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getId = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, '_id', '');
  }
};

/**
 * Gets the annotation owner.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {string} The owner for the annotation or empty string id not found.
 * @throws {Error} If annotation is null or undefined.
 */
export const getOwner = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  }
  return get(annotationObject, 'owner', '');
};

/**
 * Gets the GeoJSON that contains the geospacial information of the annotation, the information
 * related to its position and time in the video and its structure.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {object} A GeoJSON feature point with the information and structure of the
 * annotation or an empty object if this GeoJSON is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getGeoJSON = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'geoJSON', {});
  }
};

/**
 * Get coordinates annotation.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {object} A GeoJSON feature point with the information and structure of the
 * annotation or an empty object if this GeoJSON is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getCoordinates = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'geoJSON.geometry.coordinates', []);
  }
};

/**
 * Gets the id of the video to which the annotation belongs.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {string} The id of the video to which the annotation belongs or an empty string if
 * video is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getVideo = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'geoJSON.properties.video', '');
  }
};

/**
 * Gets the video time for the annotation.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {number} The video time for the annotation or 0 if video time is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getVideoTime = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'geoJSON.properties.videoTime', 0);
  }
};

/**
 * Gets the annotation template for the annotation.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {object} An object that represents the structure of the annotation or an empty object
 * if template is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getTemplate = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'geoJSON.properties.template', {});
  }
};

/**
 * Gets the position of the annotation in the video.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {object} An object that contains the position of the annotation and the resolution of
 * the video or an empty object if video position is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getVideoPosition = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'geoJSON.properties.videoPosition', {});
  }
};

/**
 * Gets the value is proyected point of the annotation in the video.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {object} An object that contains the position of the annotation and the resolution of
 * the video or an empty object if video position is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getIsProyectedPoint = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'geoJSON.properties.isProyected', false);
  }
};

/**
 * Gets the way the annotation was created.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {string} A string that represents how the annotation was created, whether this was done
 * by the user himself or by the artificial intelligence method, or an empty string if the origin
 * is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getIsTracking = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'isTracking', false);
  }
};

/**
 * Gets the way the annotation was created.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {string} A string that represents how the annotation was created, whether this was done
 * by the user himself or by the artificial intelligence method, or an empty string if the origin
 * is undefined.
 * @throws Error if annotation object is undefined.
 */
export const getOrigin = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'origin', '');
  }
};

/**
 * Checks if an annotation was created by the artificial intelligence method.
 *
 * @param {object} annotationObject - The annotation object.
 * @returns {boolean} Returns true if the annotation was created with artificial intellingence and
 * false otherwise.
 * @throws Error if annotation object is undefined.
 */
export const isAutomatic = annotationObject => {
  if (isNil(annotationObject)) {
    throw new Error('annotation not defined');
  } else {
    return get(annotationObject, 'origin', '') === 'automatic';
  }
};

export default {
  getId,
  getGeoJSON,
  getVideo,
  getVideoTime,
  getTemplate,
  getVideoPosition,
  getOrigin,
  isAutomatic
};
